import { useState, useCallback } from "react";
import debounce from "lodash/debounce";
import { Form, useOutletContext, redirect } from "react-router-dom";
import {
  FormRow,
  FormRowSelect,
  SubmitBtn,
  AutoCompleteInput,
  ImageCarousel,
} from "../components/index.js";
import Wrapper from "../assets/wrappers/DashboardFormPage.js";
import {
  RSS_NEWS,
  CITIES,
  WORK_ON_SATURDAY,
  MEMBER_TYPE,
  DID_YOU_KNOW,
} from "../../../utils/constants.js";
import { toast } from "react-toastify";
import { customFetch } from "../utils/custom-fetch.js";
import ManagerFormModal from "../components/Building/ManagerFormModal";

export const action = async ({ request }) => {
  console.log(`creating new office building`);
  
  const formData = await request.formData();
  const data = Object.fromEntries(formData);

  try {
    if (data.numberOfFloors === 0 || data.numberOfFloors === "בחר מספר קומות") {
      toast.error("חובה לבחור מספר קומות בבניין");
      return null;
    }

    // יצירת בניין משרדים במסד הנתונים
    const building = await customFetch.post("/floors-navigation", {
      ...data,
      buildingType: "משרדים",
    });
    
    const {newFloor} = building.data;
    
    toast.success("בניין המשרדים נוצר בהצלחה, יש להגדיר את הקומות");
    return redirect("/dashboard/add-floor/" + newFloor._id);
  } catch (error) {
    toast.error(error?.response?.data?.msg);
    return error;
  }
};

const AddFloorsNavigation = () => {
  const { user } = useOutletContext();
  const [formData, setFormData] = useState({
    city: "",
    street: "",
    houseNumber: "",
    manager: "",
    maintenanceCompany: "",
    rssWeatherPath: "",
    rssTitleNewsPath: "מבזקים",
    nickname: "",
    rssNewsPath: "",
    workOnSaturday: WORK_ON_SATURDAY.NO,
    memberType: MEMBER_TYPE.FREE,
    buildingType: "משרדים",
    designTemplate: "default",
    numberOfFloors: 0,
    createdBy: user._id,
  });
  const [streets, setStreets] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("default");
  const [showModal, setShowModal] = useState(false);

  const [streetSearch, setStreetSearch] = useState("");
  const [isLoadingStreets, setIsLoadingStreets] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const debouncedFetchStreets = useCallback(
    debounce(async (cityName, street = "", pageNum = 1) => {
      if (!cityName) return;
      if (cityName === "יפו" || cityName === "תל אביב")
        cityName = "תל אביב - יפו";
      try {
        setIsLoadingStreets(true);
        const response = await customFetch.get(
          `/buildings/streets/${encodeURIComponent(cityName)}`,
          {
            params: {
              search: street,
              page: pageNum,
              limit: 100,
            },
          }
        );
        const newStreets = response.data.streets;
        setStreets((prev) =>
          pageNum === 1 ? newStreets : [...prev, ...newStreets]
        );
        setHasMore(response.data.hasMore);
      } catch (error) {
        toast.error(error.response?.data?.message || "שגיאה בטעינת רחובות");
      } finally {
        setIsLoadingStreets(false);
      }
    }, 500),
    []
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleSaveManager = () => {
    setShowModal(false);
  };

  const handleCityChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      city: value,
      street: "",
    }));
    setStreets([]);
    setPage(1);
    setStreetSearch("");
  };

  const handleCitySelect = (selectedOption) => {
    const cityValue = selectedOption ? selectedOption.value : "";
    setFormData((prev) => ({
      ...prev,
      city: cityValue,
      street: "",
    }));
    setStreets([]);
    setPage(1);
    setStreetSearch("");
    if (cityValue) {
      debouncedFetchStreets(cityValue, "", 1);
    }
  };

  const handleStreetSearch = (value) => {
    setStreetSearch(value);
    setStreets([]);
    setPage(1);
    if (formData.city) {
      debouncedFetchStreets(formData.city, value, 1);
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setFormData((prevFormData) => ({
      ...prevFormData,
      designTemplate: template,
    }));
  };

  const setMaintenanceCompanyId = (companyId) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      maintenanceCompany: companyId,
    }));
  };

  return (
    <Wrapper className="full-page">
      <Form method="post" className="form">
        <h4 className="form-title">הוספת בניין משרדים</h4>

        <div className="form-center" style={{ marginTop: "20px" }}>
          <AutoCompleteInput
            options={CITIES}
            name="city"
            labelText="עיר"
            placeholder="אנא בחר עיר..."
            value={formData.city}
            onChange={(e) => handleCityChange(e.target.value)}
            onSelect={handleCitySelect}
          />
          <input type="hidden" name="city" value={formData.city} />{" "}
          <AutoCompleteInput
            options={streets}
            name="street"
            labelText="רחוב"
            placeholder="אנא בחר רחוב..."
            value={formData.street}
            onChange={(e) => {
              handleChange(e);
              handleStreetSearch(e.target.value);
            }}
            isLoading={isLoadingStreets}
          />
          <input type="hidden" name="street" value={formData.street} />
          <FormRow
            type="number"
            name="houseNumber"
            labelText="מספר בית"
            value={formData.houseNumber}
            handleChange={handleChange}
            isRequired={true}
          />
          <FormRow
            type="text"
            name="nickname"
            labelText="שם לבניין (לא חובה, יופיע במקום הכתובת אם נבחר)"
            value={formData.nickname}
            handleChange={handleChange}
            placeholder="לדוג׳: משרד הראשונים"
            isRequired={false}
          />
          <FormRowSelect
            labelText="מספר קומות בבניין (חובה)"
            type="number"
            name="numberOfFloors"
            isRequired={true}
            list={[
              "בחר מספר קומות",
              ...Array.from({ length: 51 }, (_, i) => i + 1),
            ]}
            value={formData.numberOfFloors}
            defaultValue={formData.numberOfFloors}
            handleChange={handleChange}
          />
          <FormRowSelect
            labelText="תחומי החדשות שיופיעו בכתוביות"
            name="rssTitleNewsPath"
            list={Object.values(RSS_NEWS)}
            value={formData.rssTitleNewsPath}
            handleChange={handleChange}
          />
          <FormRowSelect
            labelText="פעילות בשבת"
            name="workOnSaturday"
            list={Object.values(WORK_ON_SATURDAY)}
            value={formData.workOnSaturday}
            handleChange={handleChange}
          />
          <button
            type="button"
            className="btn btn-block"
            onClick={() => setShowModal(true)}
          >
            קישור לחברת אחזקה
          </button>
        </div>

        <div
          className="form-center"
          style={{
            marginTop: "20px",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <h3
            htmlFor="carousel"
            style={{
              textAlign: "center",
              width: "100%",
              marginBottom: "1rem",
            }}
          >
            יש לבחור תבנית עיצוב
          </h3>
          <ImageCarousel
            name="carousel"
            onTemplateSelect={handleTemplateSelect}
            selectedTemplate={selectedTemplate}
          />
        </div>

        {formData.maintenanceCompany && (
          <input
            type="hidden"
            name="maintenanceCompany"
            value={formData.maintenanceCompany}
          />
        )}
        <input type="hidden" name="designTemplate" value={selectedTemplate} />

        <div className="btn-container">
          <SubmitBtn formBtn buttonText="שמירה" />
          <button
            className="btn btn-block clear-btn"
            onClick={(e) => {
              e.preventDefault();
              setFormData({
                city: "",
                street: "",
                houseNumber: "",
                manager: "",
                maintenanceCompany: "",
                rssWeatherPath: "",
                rssTitleNewsPath: "מבזקים",
                nickname: "",
                rssNewsPath: "",
                workOnSaturday: WORK_ON_SATURDAY.NO,
                memberType: MEMBER_TYPE.FREE,
                buildingType: "משרדים",
                designTemplate: "default",
                didYouKnow: "יהדות ומסורת",
                numberOfFloors: 0,
                createdBy: user._id,
              });
            }}
          >
            ניקוי שדות
          </button>
        </div>
      </Form>

      {showModal && user.role === "user" && (
        <ManagerFormModal
          setMaintenanceCompanyId={setMaintenanceCompanyId}
          closeModal={() => setShowModal(false)}
          handleSave={handleSaveManager}
        />
      )}
    </Wrapper>
  );
};

export default AddFloorsNavigation;
